import List from "@mui/material/List";
import TextField from "@mui/material/TextField";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import { useCallback, useMemo } from "react";
import { Typography } from "@mui/material";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Virtuoso } from "react-virtuoso";
import { getPrioritizedItem } from "../../../../utils/configurationApi";

const getHashKey = (level, id) => `${level}-${id}`;

export function ConfigureNode({
  item,
  hash,
  level,
  onHashChange,
  parentId,
  toggleCollaps
}) {

  const sortedItems = useMemo(() => {
    if (!item?.items) {
      return null;
    }
    const sortedItems = item.items
      .filter((node) => !!hash[getHashKey(level, node.id)])
      .sort((a, b) => {
        const first = hash[getHashKey(level, a.id)]?.priority ?? a.priority;
        const second = hash[getHashKey(level, b.id)]?.priority ?? b.priority;
        if (first > second) {
          return 1;
        } else if (first < second) {
          return -1;
        } else return 0;
      });
    return sortedItems;
  }, [item.items, hash, level]);

  const data = useMemo(() => {
    if (!sortedItems) {
      return item;
    }
    return sortedItems[0];
  }, [item, sortedItems]);

  const childrenNodes = useMemo(() => {
    return item?.children?.filter((child) => {
      if (child.items) {
        for (let i = 0; i < child.items.length; i++) {
          if (!!hash[getHashKey(level + 1, child.items[i].id)]) {
            return true;
          }
        }
        return false;
      }
      return !!hash[getHashKey(level + 1, child.id)];
    }).sort((a, b) => {
      const first = a.items ? getPrioritizedItem(a.items) : a;
      const second = b.items ? getPrioritizedItem(b.items) : b;
      return first.name.localeCompare(second.name);
    }) || [];
  }, [hash, level, item]);

  const hashItem = useMemo(() => {
    if (!data) {
      return null;
    }
    return hash[getHashKey(level, data.id)];
  }, [hash, level, data]);

  const isChecked = useMemo(() => !!hashItem, [hashItem]);

  const itemValue = useMemo(
    () => hashItem || { ...data, level, parentId, displayOrder: "" },
    [hashItem, data, level, parentId]
  );

  const handlePriorityChange = useCallback((e, index) => {
    e.stopPropagation();
    const firstItem = hash[getHashKey(level, sortedItems[index].id)];
    const secondItem = hash[getHashKey(level, sortedItems[index + 1].id)];
    const items = [
      { ...firstItem, priority: firstItem.priority ? firstItem.priority + 1 : sortedItems[index + 1].priority },
      { ...secondItem, priority: secondItem.priority ? secondItem.priority - 1 : sortedItems[index].priority },
    ];
    onHashChange(items, isChecked);
  }, [hash, level, sortedItems, onHashChange, isChecked]);

  const handleDisplayOrderChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      onHashChange([{ ...itemValue, [name]: value }], isChecked);
    },
    [isChecked, itemValue, onHashChange]
  );

  if (!data || !isChecked) {
    return null;
  }

  return (
    <Box style={{ margin: '6px 0' }}>
      <Box sx={{ width: '100%', display: 'flex' }}>
        <List
          disablePadding
          sx={{
            background: "#f9f9f9",
            display: "flex",
            alignItems: "center",
            width: "100%"
          }}
        >
          <Divider />
          <Box sx={{ width: "70px", ml: 2 }}>
            <TextField
              sx={{ width: "100%" }}
              name="displayOrder"
              label="Order"
              value={itemValue?.displayOrder || ''}
              variant="outlined"
              size="small"
              onChange={handleDisplayOrderChange}
            />
          </Box>
          <ListItemButton
            sx={{
              paddingLeft: level,
              display: "flex",
              justifyContent: "space-between",
              gap: '10px',
            }}
            onClick={toggleCollaps}
          >
            {childrenNodes.length ? (
              item.isCollapsed ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )
            ) : (
              <div style={{ paddingRight: "24px" }}></div>
            )}
            <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', overflow: 'hidden', width: '100%' }}>
              <Typography variant="h6" sx={{ fontSize: '1rem' }} style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{data.name}</Typography>
              {sortedItems && <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', overflow: 'auto' }}>
                (
                {sortedItems.map((node, index) => <Box key={node.source} sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  {node.source}
                  {index !== sortedItems.length - 1 && <ArrowForwardIcon onClick={(e) => handlePriorityChange(e, index)} />}
                </Box>)}
                )
              </Box>}
            </Box>
          </ListItemButton>
        </List>
      </Box>
      <Collapse in={item.isCollapsed && level !== 3}>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
          {item.isCollapsed &&
            <Virtuoso
              data={childrenNodes}
              style={{ height: childrenNodes.length > 7 ? '350px' : `${childrenNodes.length * 50}px` }}
              itemContent={(_, child) => <ConfigureNode
                hash={hash}
                item={child}
                level={level + 1}
                onHashChange={onHashChange}
                parentId={data.id}
              />}
            />
          }
        </Box>
      </Collapse>
    </Box>
  );
} 
